import {
	type QuestionnaireAnswers,
	useIntl,
	type UserRequirement,
	type UserRequirementStateStatus,
	type UserUKQuizRequirementConfig,
	useSetPageTitle
} from '@capsicum/common';
import { Questionnaire } from '@capsicum/common-web';
import { useUkQuiz } from '@capsicum/features/regulations/shared';
import { useState } from 'react';
import { styled } from 'styled-components';

import { useUkQuizModals } from '../../../hooks/useUkQuizModals';
import { CredentialGuardLoader } from '../../CredentialGuardLoader';

export type UkQuizProps = {
	isForcedRequirement?: boolean;
	onDismiss: () => void;
	requirement: UserRequirement;
	status?: UserRequirementStateStatus;
};

export const UkQuiz = ({ isForcedRequirement = false, onDismiss, requirement, status }: UkQuizProps) => {
	const { $t } = useIntl();
	const [showQuiz, setShowQuiz] = useState(false);
	const { showBlockedUntilModal, showIntroductoryModal, showSuccessModal } = useUkQuizModals({
		isForcedRequirement,
		onDismiss,
		status
	});

	const config = requirement.config as UserUKQuizRequirementConfig;
	const onProceedPress = () => {
		setShowQuiz(true);
	};
	const openIntroductoryModal = () => showIntroductoryModal(onProceedPress);
	const openBlockedUntilModal = () =>
		showBlockedUntilModal(config.ukForm.attemptsLeft || null, config.ukForm.blockedUntil || '');

	const { isUkQuizSubmissionLoading, submissionResponse, submitUkQuiz } = useUkQuiz({
		openBlockedUntilModal,
		openIntroductoryModal,
		quizContent: config.ukForm.form,
		showSuccessModal
	});

	useSetPageTitle($t('jurisdictionRequirements.ukQuiz.title'));

	const submitQuiz = (answers: QuestionnaireAnswers) => {
		if (!requirement.identifier) return;
		submitUkQuiz(requirement.identifier, answers);
	};

	if (!showQuiz || submissionResponse || !config.ukForm.form) return <CredentialGuardLoader />;

	return (
		<S.Wrapper>
			<Questionnaire
				content={config.ukForm.form}
				customBackNavigation={onDismiss}
				isLoading={isUkQuizSubmissionLoading}
				onlyButtonsNavigation={isForcedRequirement}
				submit={submitQuiz}
				testID="uk-quiz"
			/>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		height: 75dvh;
		margin-top: ${({ theme }) => theme.spacings.medium};
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			height: 70dvh;
		}
	`;
}
